/**
 * RecordTransactionModal.jsx — v3.7.15 Phase 84.32 Part 1
 *
 * Focused modal for adding a single manual transaction. Replaces the
 * always-visible RecordManualTxnCard on the dashboard scroll (which
 * has been retired per spec).
 *
 * Fields
 * ------
 *   • Type: Expense · Income · Transfer
 *   • Amount + Currency (defaults from selected account)
 *   • Date (defaults to today, cannot be in the future)
 *   • Account
 *   • Description
 *   • Category (only for Expense/Income)
 *   • Optional note
 *
 * Quick entry
 * -----------
 * A compact "Recent or frequent" chip strip inside the modal prefills
 * the form — it does NOT auto-save (per spec). We use the 3 most
 * recent + 3 most frequent expenses from the caller's transactions
 * history, deduped by (description, amount).
 *
 * Manual audit trail
 * ------------------
 * The row is saved with `source='manual'` (backend reconciliation
 * engine hooks off this to reconcile against imported statements
 * without double-counting).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';

const TXN_TYPES = [
  { value: 'expense',  label: 'Expense' },
  { value: 'income',   label: 'Income' },
  { value: 'transfer', label: 'Transfer' },
];

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function buildQuickChips(txns = []) {
  // 3 most recent expenses + 3 most frequent (by description).
  const rec = (txns || [])
    .filter((t) => t?.type === 'expense' && t?.description)
    .slice(0, 3)
    .map((t) => ({ label: t.description, amount: t.amount, category: t.category, currency: t.currency }));
  const freq = new Map();
  (txns || []).forEach((t) => {
    if (t?.type !== 'expense' || !t?.description) return;
    const k = String(t.description).toLowerCase().slice(0, 40);
    const cur = freq.get(k) || { label: t.description, amount: t.amount, category: t.category, currency: t.currency, count: 0 };
    cur.count += 1;
    freq.set(k, cur);
  });
  const freqArr = [...freq.values()].sort((a, b) => b.count - a.count).slice(0, 3);
  const dedup = new Map();
  [...rec, ...freqArr].forEach((c) => {
    const k = `${c.label}|${c.amount}`;
    if (!dedup.has(k)) dedup.set(k, c);
  });
  return [...dedup.values()].slice(0, 6);
}

export default function RecordTransactionModal({ open, onOpenChange, accounts = [], txns = [], onSaved }) {
  const { user } = useAuth();
  const chips = useMemo(() => buildQuickChips(txns), [txns]);

  const [type,        setType]        = useState('expense');
  const [amount,      setAmount]      = useState('');
  const [currency,    setCurrency]    = useState('');
  const [date,        setDate]        = useState(todayISO());
  const [accountId,   setAccountId]   = useState('');
  const [description, setDescription] = useState('');
  const [category,    setCategory]    = useState('');
  const [note,        setNote]        = useState('');
  const [saving,      setSaving]      = useState(false);

  // The currency shown always follows the selected account — a transaction
  // does not carry its own currency in the schema.
  useEffect(() => {
    const acc = accounts.find((a) => a.id === accountId);
    setCurrency(acc?.currency || '');
  }, [accountId, accounts]);

  const reset = () => {
    setType('expense'); setAmount(''); setCurrency(''); setDate(todayISO());
    setAccountId(''); setDescription(''); setCategory(''); setNote(''); setSaving(false);
  };

  const applyChip = (c) => {
    setDescription(c.label);
    setAmount(String(c.amount ?? ''));
    if (c.category) setCategory(c.category);
  };

  const save = async () => {
    if (!user) return;
    const amt = Number(amount);
    if (!amt || !Number.isFinite(amt) || amt <= 0) { toast.error('Enter an amount greater than 0.'); return; }
    if (!date) { toast.error('Pick a date.'); return; }
    if (new Date(date).getTime() > Date.now() + 86400000) { toast.error('Date cannot be in the future.'); return; }
    setSaving(true);
    const row = {
      user_id:     user.id,
      type,
      amount:      amt,
      txn_date:    date,
      account_id:  accountId || null,
      description: (description || '').trim() || null,
      category:    (category    || '').trim() || null,
      note:        (note        || '').trim() || null,
      source:      'manual',
    };
    const { data, error } = await supabase.from('transactions').insert(row).select('id').single();
    setSaving(false);
    if (error) {
      toast.error(error.message || 'Could not save.');
      return;
    }
    // Undo affordance (5s window) — deletes the row we just wrote.
    toast.success('Transaction recorded.', {
      action: {
        label: 'Undo',
        onClick: async () => {
          try {
            await supabase.from('transactions').delete().eq('id', data.id);
            toast('Undone.');
            onSaved?.();
          } catch { /* silent */ }
        },
      },
      duration: 9000,
    });
    onSaved?.();
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent
        className="sm:max-w-lg rounded-2xl"
        data-testid="record-txn-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">Record transaction</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Type toggle */}
          <div className="flex gap-2" role="tablist" aria-label="Transaction type">
            {TXN_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={type === t.value}
                onClick={() => setType(t.value)}
                className={
                  'flex-1 py-2 rounded-xl border text-sm font-medium transition-colors ' +
                  (type === t.value
                    ? 'border-primary bg-primary/5 text-charcoal'
                    : 'border-border/60 text-muted-foreground hover:bg-muted/40')
                }
                data-testid={`record-txn-type-${t.value}`}
              >{t.label}</button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="rt-amount" className="text-[11px] uppercase tracking-widest text-muted-foreground/70 mb-1 block">Amount</label>
              <Input id="rt-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="record-txn-amount" />
            </div>
            <div>
              <label htmlFor="rt-currency" className="text-[11px] uppercase tracking-widest text-muted-foreground/70 mb-1 block">Currency</label>
              {/* v3.19.2 — a transaction has no currency of its own; it takes
                  the currency of its account (there is no transactions.currency
                  column, and writing one made every save fail). Shown, not
                  edited. */}
              <div
                id="rt-currency"
                className="w-full h-9 rounded-lg border border-input bg-muted/40 px-3 text-sm flex items-center text-muted-foreground"
                data-testid="record-txn-currency"
              >
                {currency || 'Follows the account'}
              </div>
            </div>
            <div>
              <label htmlFor="rt-date" className="text-[11px] uppercase tracking-widest text-muted-foreground/70 mb-1 block">Date</label>
              <Input id="rt-date" type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} data-testid="record-txn-date" />
            </div>
            <div>
              <label htmlFor="rt-account" className="text-[11px] uppercase tracking-widest text-muted-foreground/70 mb-1 block">Account</label>
              <select id="rt-account" value={accountId} onChange={(e) => setAccountId(e.target.value)} className="w-full h-9 rounded-lg border border-input bg-background px-3 text-sm" data-testid="record-txn-account">
                <option value="">Choose an account…</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="rt-desc" className="text-[11px] uppercase tracking-widest text-muted-foreground/70 mb-1 block">Description</label>
            <Input id="rt-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Uber, Rent, Salary" data-testid="record-txn-desc" />
          </div>

          {type !== 'transfer' && (
            <div>
              <label htmlFor="rt-category" className="text-[11px] uppercase tracking-widest text-muted-foreground/70 mb-1 block">Category</label>
              <Input id="rt-category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder={type === 'income' ? 'Salary, Freelance…' : 'Food, Bills, Fuel…'} data-testid="record-txn-category" />
            </div>
          )}

          <div>
            <label htmlFor="rt-note" className="text-[11px] uppercase tracking-widest text-muted-foreground/70 mb-1 block">Note (optional)</label>
            <Textarea id="rt-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Anything else worth remembering?" data-testid="record-txn-note" />
          </div>

          {/* Recent / frequent quick entry (inside modal — never auto-saves) */}
          {chips.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground/70 mb-2">Recent or frequent</div>
              <div className="flex flex-wrap gap-1.5" data-testid="record-txn-chips">
                {chips.map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => applyChip(c)}
                    className="text-[12px] px-2.5 py-1 rounded-full border border-border/70 hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-primary/40"
                    data-testid={`record-txn-chip-${i}`}
                  >
                    {c.label} · {c.currency || ''} {c.amount}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="record-txn-cancel">
            Cancel
          </Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="record-txn-save">
            {saving ? 'Saving…' : 'Save transaction'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
